import { WebSocketMessage } from '../types';

export class WebSocketService {
  private socket: WebSocket | null = null;
  private reconnectInterval: number = 5000;
  private isConnecting: boolean = false;
  private messageHandlers: Map<string, ((data: any) => void)[]> = new Map();

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isConnecting || (this.socket && this.socket.readyState === WebSocket.OPEN)) {
        resolve();
        return;
      }

      this.isConnecting = true;
      
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.denisphones.org';
      const protocol = apiUrl.startsWith('https') ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${apiUrl.replace(/^https?:\/\//, '')}`;
      
      console.log('Connecting to WebSocket:', wsUrl);
      
      try {
        // WebSocket автоматически передает cookies, но нужно убедиться, что они включены
        this.socket = new WebSocket(wsUrl);
        
        this.socket.onopen = () => {
          console.log('✅ WebSocket connection established to:', wsUrl);
          this.isConnecting = false;
          this.emit('connected');
          resolve();
        };
        
        this.socket.onclose = (event) => {
          console.log('❌ WebSocket connection closed:', event.code, event.reason);
          
          // Если соединение закрыто из-за неавторизованного доступа
          if (event.code === 1008 && event.reason === 'Unauthorized') {
            console.log('🔒 WebSocket connection closed due to unauthorized access');
            this.isConnecting = false;
            this.emit('unauthorized');
            // Перенаправляем на страницу логина
            setTimeout(() => {
              window.location.href = '/login';
            }, 1000);
            return;
          }
          
          // Уведомляем о разрыве соединения
          this.emit('disconnect');
          
          console.log('Attempting to reconnect in', this.reconnectInterval / 1000, 'seconds...');
          this.isConnecting = false;
          this.emit('disconnected');
          setTimeout(() => this.connect(), this.reconnectInterval);
        };
        
        this.socket.onmessage = (event) => {
          try {
            const data: WebSocketMessage = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (error) {
            console.error('❌ Error parsing WebSocket message:', error, 'Raw data:', event.data);
          }
        };
        
        this.socket.onerror = (error) => {
          console.error('❌ WebSocket error:', error);
          this.isConnecting = false;
          this.emit('error', error);
          reject(error);
        };
        
      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

  send(message: string): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(message);
    } else {
      console.warn('WebSocket is not connected');
    }
  }

  private handleMessage(data: WebSocketMessage): void {
    switch (data.type) {
      case 'existing_logs':
        this.emit('existing_logs', data.logs);
        break;
      case 'new_log':
        this.emit('new_log', data.log);
        break;
      case 'status':
        this.emit('status', data.message);
        break;
      case 'connection_established':
        this.emit('connected');
        this.emit('connection_established', data);
        break;
      case 'ping':
        // Отвечаем на ping от сервера
        this.send(JSON.stringify({ type: 'pong', timestamp: Date.now() }));
        break;
      case 'pong':
        // Получили pong от сервера
        this.emit('pong', data);
        break;
      case 'new_chat_message':
        this.emit('new_chat_message', data.message);
        break;
    }
  }

  on(event: string, handler: (data: any) => void): void {
    if (!this.messageHandlers.has(event)) {
      this.messageHandlers.set(event, []);
    }
    this.messageHandlers.get(event)!.push(handler);
  }

  off(event: string, handler: (data: any) => void): void {
    const handlers = this.messageHandlers.get(event);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  private emit(event: string, data?: any): void {
    const handlers = this.messageHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => handler(data));
    }
  }

  get isConnected(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }
}

export const wsService = new WebSocketService();
